// Universal user QR widget: data-365="my-qr"
// Shows the signed-in user's ONE rotating identity QR — the same "this is me" code the whole platform
// resolves (front-desk check-in, scoreboard team-join, stat-claim). Unlike the member-card widget this is
// NOT venue-scoped and carries no membership data; it fetches GET /api/me/qr-token (Bearer) and rotates the
// 60s-window bundle locally (server time, so phone-clock skew is irrelevant). A screenshot dies in minutes.
// The QR is rendered LOCALLY (vendored qrcode-generator) — no external QR service sees the token. There is
// no static fallback: the identity token is always rotating. See docs/plans/UNIVERSAL_USER_QR_PLAN.md.
import { Api } from '../api';
import { Auth, AuthRequiredError } from '../auth';
import type { EmbedConfig, ThemeOptions } from '../types';
import { createRoot, h, footer, clear, renderLoading, signOutRow } from '../ui';
import qrcode from '../vendor/qrcode-generator';

interface UserQrTokenDto {
  windowSeconds: number;
  serverTimeUtc: string;
  secondsLeftInWindow: number;
  tokens: string[];
}

const QR_SIZE = 240;
const WINDOW_SECONDS = 60;

/** Render a QR locally and return a data URL ('' on failure). */
function qrDataUrl(text: string): string {
  try {
    const qr = qrcode(0, 'M');
    qr.addData(text);
    qr.make();
    const n = qr.getModuleCount();
    const cell = Math.max(2, Math.floor(QR_SIZE / (n + 4)));
    return qr.createDataURL(cell, cell * 2);
  } catch {
    return '';
  }
}

// Rotation timers from a previous render (sign-out/re-render must not leak intervals).
let activeTimers: number[] = [];
function clearTimers(): void {
  for (const t of activeTimers) window.clearInterval(t);
  activeTimers = [];
}

export function mountMyQr(mount: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth, theme: ThemeOptions): void {
  const { root } = createRoot(mount, theme);
  const pending = auth.peekPending<{ t?: string }>();
  if (pending && pending.t === 'my-qr') auth.clearPending();
  void render(root, cfg, api, auth);
}

async function render(root: HTMLElement, cfg: EmbedConfig, api: Api, auth: Auth): Promise<void> {
  clearTimers();
  clear(root);
  root.append(h('h3', undefined, 'My QR Code'));

  if (!auth.isLoggedIn) {
    root.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Sign in to show your personal 365 QR code.'));
    const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
    login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-qr' }));
    root.append(login, footer());
    return;
  }

  const host = h('div');
  root.append(host);
  renderLoading(host, 'My QR Code');

  let dto: UserQrTokenDto | null = null;
  try {
    dto = await api.get<UserQrTokenDto>('/api/me/qr-token');
  } catch (e) {
    clear(host);
    if (e instanceof AuthRequiredError) {
      host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' }, 'Please sign in again to view your QR code.'));
      const login = h('button', { class: 'e365-btn' }, 'Sign in') as HTMLButtonElement;
      login.addEventListener('click', () => auth.redirectToLogin({ t: 'my-qr' }));
      host.append(login, footer());
    } else {
      host.append(h('div', { class: 'e365-alert error' }, 'We couldn’t load your QR code. Please try again.'));
      const retry = h('button', { class: 'e365-btn' }, 'Try again') as HTMLButtonElement;
      retry.addEventListener('click', () => void render(root, cfg, api, auth));
      host.append(retry, footer());
    }
    return;
  }

  clear(host);
  host.append(h('div', { class: 'e365-muted', style: 'margin:-4px 0 10px' },
    'Show this at a venue to check in, or at the scoreboard to join a game.'));
  host.append(renderQr(dto, api, auth.profile));
  host.append(
    signOutRow(auth.profile?.email, () => { auth.logout(); void render(root, cfg, api, auth); }),
    footer(),
  );
}

function renderQr(initial: UserQrTokenDto, api: Api, profile: { name: string; email: string } | null): HTMLElement {
  const card = h('div', {
    style: 'border:1px solid var(--e365-border); border-radius:var(--e365-radius); padding:14px; margin-bottom:10px; background:var(--e365-surface); text-align:center;',
  });
  // Whose card this is — staff glance at the name while the code is scanned (member-card parity).
  const who = (profile?.name || '').trim() || (profile?.email || '').trim();
  if (who) card.append(h('div', { style: 'font-weight:700; font-size:1.1rem; margin-bottom:8px;' }, who));
  const img = h('img', {
    width: String(QR_SIZE),
    height: String(QR_SIZE),
    alt: 'My 365 QR code',
    style: 'border:1px solid var(--e365-border); border-radius:8px; padding:6px; background:#fff; max-width:100%; height:auto; image-rendering:pixelated;',
  }) as HTMLImageElement;
  const caption = h('div', { class: 'e365-muted', style: 'font-size:.78rem; margin-top:6px;' }, 'Live code');
  card.append(img, caption);
  void rotate(initial, img, caption, api);
  return card;
}

/** Drive the rotating QR: swap at window boundaries, top up near the end. */
async function rotate(initial: UserQrTokenDto, img: HTMLImageElement, caption: HTMLElement, api: Api): Promise<void> {
  let tokens: string[] = [];
  let baseWindow = 0;          // unix/60 of tokens[0]
  let serverUnixAtFetch = 0;
  let clientMsAtFetch = 0;
  let shownIdx = -1;
  let fetching = false;

  const apply = (dto: UserQrTokenDto): boolean => {
    if (!dto || !dto.tokens || dto.tokens.length === 0) return false;
    tokens = dto.tokens;
    serverUnixAtFetch = Math.floor(new Date(dto.serverTimeUtc).getTime() / 1000);
    clientMsAtFetch = Date.now();
    baseWindow = Math.floor(serverUnixAtFetch / WINDOW_SECONDS);
    shownIdx = -1;
    return true;
  };

  const fetchBundle = async (): Promise<boolean> => {
    if (fetching) return false;
    fetching = true;
    try {
      const dto = await api.get<UserQrTokenDto>('/api/me/qr-token');
      return apply(dto);
    } catch {
      return false;
    } finally {
      fetching = false;
    }
  };

  const tick = async (): Promise<void> => {
    const serverUnix = serverUnixAtFetch + Math.floor((Date.now() - clientMsAtFetch) / 1000);
    let idx = Math.floor(serverUnix / WINDOW_SECONDS) - baseWindow;
    const secondsLeft = WINDOW_SECONDS - (serverUnix % WINDOW_SECONDS);

    if (idx >= tokens.length - 1) await fetchBundle(); // entering the last window → top up

    const nowUnix = serverUnixAtFetch + Math.floor((Date.now() - clientMsAtFetch) / 1000);
    idx = Math.floor(nowUnix / WINDOW_SECONDS) - baseWindow;

    let stale = false;
    if (idx >= tokens.length) {
      idx = tokens.length - 1; // offline: server grants ±1 window of grace on the last token
      stale = true;
    }
    if (idx !== shownIdx) {
      shownIdx = idx;
      img.src = qrDataUrl(tokens[idx]);
    }
    caption.textContent = stale
      ? 'Reconnect to refresh your code'
      : `Live code · refreshes in ${secondsLeft}s`;
  };

  // Seed from the bundle the render already fetched, then heartbeat.
  if (apply(initial)) {
    await tick();
    activeTimers.push(window.setInterval(() => void tick(), 1000));
  }
}
